import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, Save, Plus, Pencil, Trash2, Star, Lock, Eye, EyeOff, Mail, Send } from "lucide-react";

const empty = {
  company_name: "TULSI TRADING AND LOGISTICS",
  address: "62 RAJA SC MULLICK ROAD, 1ST FLOOR",
  city: "KOLKATA - 700084",
  state: "West Bengal",
  state_code: "19",
  email: "tulsitradinglogistics@gmail.com",
  gstin: "19AAZFT4912H1ZI",
  pan: "AAZFT4912H",
  bank_name: "",
  account_number: "",
  ifsc: "",
  branch: "",
  logo_base64: "",
  stamp_base64: "",
  signature_base64: "",
  smtp_email: "",
  smtp_app_password: "",
  smtp_sender_name: "",
  smtp_configured: false,
};

const emptyBank = { bank_name: "", account_number: "", account_holder: "TULSI TRADING AND LOGISTICS", ifsc: "", branch: "", is_default: false };

export default function Settings() {
  const [form, setForm] = useState(empty);
  const [banks, setBanks] = useState([]);
  const [bankOpen, setBankOpen] = useState(false);
  const [bankForm, setBankForm] = useState(emptyBank);
  const [editBankId, setEditBankId] = useState(null);
  const fileRef = useRef(null);
  const stampRef = useRef(null);
  // ─── Change-password state ───
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [pwShow, setPwShow] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [showAppPw, setShowAppPw] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const loadAll = async () => {
    const [s, b] = await Promise.all([api.get("/settings"), api.get("/bank-accounts")]);
    setForm({ ...empty, ...s.data });
    setBanks(b.data);
  };
  useEffect(() => { loadAll(); }, []);

  const onImageFile = (key) => (file) => {
    if (!file) return;
    if (file.size > 1024 * 1024 * 2) return toast.error("Image too large (max 2MB)");
    const r = new FileReader();
    r.onload = () => setForm((s) => ({ ...s, [key]: r.result }));
    r.readAsDataURL(file);
  };
  const onFile = onImageFile("logo_base64");

  const save = async () => {
    try { await api.put("/settings", form); toast.success("Settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveBank = async () => {
    if (!bankForm.bank_name || !bankForm.account_number) return toast.error("Bank name and account number required");
    try {
      if (editBankId) await api.put(`/bank-accounts/${editBankId}`, bankForm);
      else await api.post("/bank-accounts", bankForm);
      toast.success("Bank saved");
      setBankOpen(false); setBankForm(emptyBank); setEditBankId(null);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeBank = async (id) => {
    if (!confirm("Delete this bank account?")) return;
    await api.delete(`/bank-accounts/${id}`); loadAll();
  };

  const sendTestEmail = async () => {
    if (!testTo || !testTo.includes("@")) return toast.error("Enter a valid recipient email to test");
    // Save current settings first so the SMTP creds are persisted
    setTestBusy(true);
    try {
      await api.put("/settings", form);
      await api.post("/settings/email-test", { to: testTo.trim(), subject: "Test — Gmail connection OK", message: "Hello from your Tulsi Trading & Logistics account. If you can read this, Gmail is wired up correctly." });
      toast.success(`Test email sent to ${testTo}`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send test email");
    } finally {
      setTestBusy(false);
    }
  };

  const submitPasswordChange = async () => {    if (pwForm.new_password !== pwForm.confirm_password) return toast.error("New passwords don't match");
    if (pwForm.new_password.length < 8) return toast.error("Password must be at least 8 characters");
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success("Password changed. Please log in again with your new password.");
      setPwOpen(false);
      setPwForm({ current_password: "", new_password: "", confirm_password: "" });
      setTimeout(() => { logout(); nav("/login"); }, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change password");
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-4xl mx-auto" data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Company branding, logo, and bank accounts — used on every invoice & payment record." />

      <Card className="rounded-sm border-brand-line shadow-none p-6 mb-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted mb-4">Company Logo</div>
        <div className="flex items-center gap-6">
          <div className="w-32 h-32 border border-brand-line rounded-sm flex items-center justify-center bg-secondary/50">
            {form.logo_base64 ? (
              <img src={form.logo_base64} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="logo-preview" />
            ) : (
              <span className="text-xs text-brand-muted text-center px-2">No logo<br/>uploaded</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => onFile(e.target.files?.[0])} data-testid="logo-input" />
            <Button onClick={() => fileRef.current?.click()} className="rounded-sm bg-brand-forest text-white" data-testid="upload-logo-btn">
              <Upload className="h-4 w-4 mr-1.5" /> Upload Logo
            </Button>
            {form.logo_base64 && <Button variant="outline" onClick={() => setForm({ ...form, logo_base64: "" })} className="rounded-sm">Remove</Button>}
            <div className="text-xs text-brand-muted">PNG / JPG, max 2MB. Shown on every bill.</div>
          </div>
        </div>
      </Card>

      {/* Stamp / Seal */}
      <Card className="rounded-sm border-brand-line shadow-none p-6 mb-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted mb-4">Company Stamp / Seal</div>
        <div className="flex items-center gap-6">
          <div className="w-36 h-36 border border-brand-line rounded-sm flex items-center justify-center bg-secondary/50">
            {form.stamp_base64 ? (
              <img src={form.stamp_base64} alt="Stamp" className="max-w-full max-h-full object-contain" data-testid="stamp-preview" />
            ) : (
              <span className="text-xs text-brand-muted text-center px-2">No stamp<br/>uploaded</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <input ref={stampRef} type="file" accept="image/*" hidden
              onChange={(e) => onImageFile("stamp_base64")(e.target.files?.[0])} data-testid="stamp-input" />
            <Button onClick={() => stampRef.current?.click()} className="rounded-sm bg-brand-forest text-white" data-testid="upload-stamp-btn">
              <Upload className="h-4 w-4 mr-1.5" /> Upload Stamp / Seal
            </Button>
            {form.stamp_base64 && <Button variant="outline" onClick={() => setForm({ ...form, stamp_base64: "" })} className="rounded-sm">Remove</Button>}
            <div className="text-xs text-brand-muted leading-relaxed max-w-md">
              The round company seal (with signature inside). Printed bottom-right on every bill PDF, above "Authorised Signatory". For best results upload a tightly-cropped PNG with a transparent background, ~600×600 px.
            </div>
          </div>
        </div>
      </Card>

      <Card className="rounded-sm border-brand-line shadow-none p-6 mb-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted mb-4">Company Identity</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Company Name *"><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></Field>
          <Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Address Line"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
          <Field label="City / Pincode"><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
          <Field label="State"><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></Field>
          <Field label="State Code"><Input value={form.state_code} onChange={(e) => setForm({ ...form, state_code: e.target.value })} /></Field>
          <Field label="GSTIN"><Input className="font-mono uppercase" value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} /></Field>
          <Field label="PAN"><Input className="font-mono uppercase" value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} /></Field>
        </div>
        <div className="flex justify-end mt-5">
          <Button onClick={save} data-testid="save-settings-btn" className="rounded-sm bg-brand-forest hover:bg-brand-forestDark text-white">
            <Save className="h-4 w-4 mr-1.5" /> Save Identity
          </Button>
        </div>
      </Card>

      {/* Bank Accounts */}
      <Card className="rounded-sm border-brand-line shadow-none mb-6 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-brand-line">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted">Bank Accounts</div>
            <div className="font-serif text-xl text-brand-ink mt-0.5">Manage receiving / paying banks</div>
          </div>
          <Button data-testid="add-bank-btn" onClick={() => { setEditBankId(null); setBankForm(emptyBank); setBankOpen(true); }} className="rounded-sm bg-brand-forest hover:bg-brand-forestDark text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Add Bank
          </Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
            <tr>
              <th className="text-left px-5 py-2.5">Bank</th>
              <th className="text-left px-3 py-2.5">Account No.</th>
              <th className="text-left px-3 py-2.5">IFSC</th>
              <th className="text-left px-3 py-2.5">Branch</th>
              <th className="px-5 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {banks.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-brand-muted text-sm">No bank accounts yet. Add one to record payments.</td></tr>}
            {banks.map((b) => (
              <tr key={b.id} className="border-t border-brand-line" data-testid={`bank-row-${b.id}`}>
                <td className="px-5 py-3 font-medium">
                  {b.bank_name}
                  {b.is_default && <Badge className="ml-2 bg-brand-sand/20 text-brand-terracotta rounded-sm"><Star className="h-3 w-3 mr-1" />Default</Badge>}
                </td>
                <td className="px-3 py-3 font-mono text-xs">{b.account_number}</td>
                <td className="px-3 py-3 font-mono text-xs">{b.ifsc || "—"}</td>
                <td className="px-3 py-3 text-brand-muted">{b.branch || "—"}</td>
                <td className="px-5 py-3 text-right">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditBankId(b.id); setBankForm({ ...emptyBank, ...b }); setBankOpen(true); }}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-brand-terracotta" onClick={() => removeBank(b.id)} data-admin-only="true">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Email / Gmail SMTP */}
      <Card className="rounded-sm border-brand-line shadow-none p-6 mb-6" data-testid="gmail-card">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted mb-4 flex items-center gap-2">
          <Mail className="h-3 w-3" /> Email (Gmail)
          {form.smtp_configured
            ? <Badge className="bg-brand-forest text-white rounded-sm text-[9px] tracking-wider">CONNECTED</Badge>
            : <Badge className="bg-brand-sand/30 text-brand-ink rounded-sm text-[9px] tracking-wider">NOT SET UP</Badge>}
        </div>
        <div className="text-xs text-brand-muted mb-4 leading-relaxed">
          Send bills and statements straight from <span className="font-medium">your own Gmail</span>. You'll need a 16-char{" "}
          <span className="font-medium">App Password</span>. Steps:
          <ol className="list-decimal ml-5 mt-2 space-y-0.5">
            <li>Turn ON 2-Step Verification for your Google account.</li>
            <li>Visit <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline">myaccount.google.com/apppasswords</a> and create a new app password named "Tulsi Trading".</li>
            <li>Copy the 16-character password (with or without spaces) and paste it below.</li>
          </ol>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Gmail Address</Label>
            <Input type="email" data-testid="smtp-email"
              value={form.smtp_email || ""}
              onChange={(e) => setForm({ ...form, smtp_email: e.target.value })}
              className="mt-1.5 h-10 rounded-sm bg-white"
              placeholder="you@gmail.com" />
          </div>
          <div>
            <Label>Sender Name (display in From)</Label>
            <Input data-testid="smtp-sender-name"
              value={form.smtp_sender_name || ""}
              onChange={(e) => setForm({ ...form, smtp_sender_name: e.target.value })}
              className="mt-1.5 h-10 rounded-sm bg-white"
              placeholder="TULSI TRADING AND LOGISTICS" />
          </div>
          <div className="md:col-span-2">
            <Label>App Password (16 chars, spaces allowed)</Label>
            <div className="relative">
              <Input type={showAppPw ? "text" : "password"} data-testid="smtp-app-password"
                value={form.smtp_app_password || ""}
                onChange={(e) => setForm({ ...form, smtp_app_password: e.target.value })}
                className="mt-1.5 h-10 rounded-sm bg-white pr-10 font-mono"
                placeholder={form.smtp_configured ? "•••••••••••••••• (stored — leave blank to keep)" : "xxxx xxxx xxxx xxxx"} />
              <button type="button" onClick={() => setShowAppPw(!showAppPw)}
                className="absolute right-2 top-1/2 -translate-y-1/2 mt-[3px] text-brand-muted hover:text-brand-ink">
                {showAppPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <div className="text-[10px] text-brand-muted mt-1">
              Never use your regular Gmail password — Google will reject it. Stored securely; not shown after saving.
            </div>
          </div>
        </div>
        <div className="mt-5 pt-4 border-t border-brand-line flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <Label>Send test to</Label>
            <Input type="email" data-testid="smtp-test-to"
              value={testTo} onChange={(e) => setTestTo(e.target.value)}
              className="mt-1.5 h-10 rounded-sm bg-white"
              placeholder="test@example.com" />
          </div>
          <Button onClick={sendTestEmail} disabled={testBusy}
            data-testid="smtp-test-send-btn"
            className="rounded-sm bg-brand-forest text-white h-10">
            <Send className="h-3.5 w-3.5 mr-1.5" /> {testBusy ? "Sending…" : "Save + Send Test"}
          </Button>
        </div>
      </Card>

      {/* Force-change banner for admin-provisioned accounts */}
      {user?.must_change_password && (
        <Card className="rounded-sm border-brand-terracotta bg-brand-terracotta/8 shadow-none p-4 mb-6"
              data-testid="must-change-password-banner">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-medium text-brand-terracotta">Password change required</div>
              <div className="text-xs text-brand-muted mt-0.5">
                Your account was created with a temporary password. Please set your own password now.
                You can access the rest of the app once you've done that.
              </div>
            </div>
            <Button onClick={() => setPwOpen(true)} data-testid="must-change-cta"
              className="rounded-sm bg-brand-terracotta text-white hover:bg-brand-terracotta/90">
              <Lock className="h-3.5 w-3.5 mr-1.5" /> Change Password Now
            </Button>
          </div>
        </Card>
      )}

      {/* Security / Change Password */}
      <Card className="rounded-sm border-brand-line shadow-none p-6 mb-6" data-testid="security-card">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted mb-4 flex items-center gap-2">
          <Lock className="h-3 w-3" /> Security
        </div>
        <div className="flex items-center justify-between gap-6">
          <div>
            <div className="font-medium">Account password</div>
            <div className="text-xs text-brand-muted mt-1">
              Signed in as <span className="font-mono">{user?.email || "—"}</span>. Choose a strong password at least 8 characters long.
            </div>
          </div>
          <Button onClick={() => setPwOpen(true)} data-testid="open-change-password-btn"
            className="rounded-sm bg-brand-forest text-white">
            <Lock className="h-3.5 w-3.5 mr-1.5" /> Change Password
          </Button>
        </div>
      </Card>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="rounded-sm" data-testid="change-password-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Change Password</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Current password">
              <div className="relative">
                <Input type={pwShow ? "text" : "password"} data-testid="current-password-input"
                  value={pwForm.current_password}
                  onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                  className="pr-9" />
                <button type="button" onClick={() => setPwShow(!pwShow)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted hover:text-brand-ink">
                  {pwShow ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </Field>
            <Field label="New password (min 8 chars)">
              <Input type={pwShow ? "text" : "password"} data-testid="new-password-input"
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} />
            </Field>
            <Field label="Confirm new password">
              <Input type={pwShow ? "text" : "password"} data-testid="confirm-password-input"
                value={pwForm.confirm_password}
                onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })} />
            </Field>
            <div className="text-xs text-brand-muted leading-relaxed pt-1">
              You'll be signed out after a successful change and asked to sign back in with the new password.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={submitPasswordChange} disabled={pwBusy} data-testid="submit-change-password-btn"
              className="rounded-sm bg-brand-forest text-white">
              {pwBusy ? "Updating…" : "Update Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bankOpen} onOpenChange={setBankOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editBankId ? "Edit" : "Add"} Bank Account</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Bank Name *"><Input data-testid="bank-name-input" value={bankForm.bank_name} onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })} /></Field>
            <Field label="Account Number *"><Input data-testid="bank-account-input" className="font-mono" value={bankForm.account_number} onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })} /></Field>
            <Field label="Account Holder"><Input value={bankForm.account_holder} onChange={(e) => setBankForm({ ...bankForm, account_holder: e.target.value })} /></Field>
            <Field label="IFSC"><Input className="font-mono uppercase" value={bankForm.ifsc} onChange={(e) => setBankForm({ ...bankForm, ifsc: e.target.value.toUpperCase() })} /></Field>
            <div className="md:col-span-2"><Field label="Branch"><Input value={bankForm.branch} onChange={(e) => setBankForm({ ...bankForm, branch: e.target.value })} /></Field></div>
            <label className="md:col-span-2 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={bankForm.is_default} onChange={(e) => setBankForm({ ...bankForm, is_default: e.target.checked })} />
              Mark as default bank (used on bills + payments)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBankOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={saveBank} data-testid="save-bank-btn" className="rounded-sm bg-brand-forest text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.15em] text-brand-muted">{label}</Label>
      <div className="mt-1.5 [&_input]:h-10 [&_input]:rounded-sm [&_input]:bg-white">{children}</div>
    </div>
  );
}
